import { useEffect, useRef, useCallback } from "react";
import { useIdentity } from "@/auth/identity";
import { useDriver } from "@/context/DriverContext";
import { setupApiClient } from "@/services/apiClient";
import { tripService } from "@/services/api";
import {
  getUserScopeFromStorage,
  saveUserScopeToStorage,
  clearUserScopeFromStorage,
  shouldRefreshScope,
  SCOPE_INVALIDATED_EVENT,
} from "@/utils/scopeStorage";
import { getTokenIssuedAtMs } from "@/utils/jwt";
import { isBlockedForNoRole } from "@/auth/claims";

// Componente para configurar el cliente de API y cargar scope (cache + iat + evento 401)
export const ApiClientConfig = () => {
  const { getAccessTokenSilently, isAuthenticated, user, logout, provider } = useIdentity();
  const { setApiClientReady, setUserScope, setDriverProfile, isApiClientReady } =
    useDriver();
  const userId = user?.sub ?? null;
  // Sin rol la API rechaza todo con 401: no se pide nada (la ruta protegida muestra "sin acceso").
  const conRol = !isBlockedForNoRole(provider, user, import.meta.env.VITE_AUTH0_AUDIENCE);
  const hasLoadedScopeRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => {
        setupApiClient(getAccessTokenSilently, () =>
          logout({ logoutParams: { returnTo: window.location.origin } })
        );
        setApiClientReady(true);
      }, 100);
    } else {
      setApiClientReady(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, logout, setApiClientReady]);

  const loadScope = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const tokenIatMs = getTokenIssuedAtMs(token);
      const cached = getUserScopeFromStorage(userId);
      if (cached && !shouldRefreshScope(tokenIatMs, userId)) {
        setUserScope(cached);
        return;
      }
      const response = await tripService.getUserScope();
      const scope = response.data?.data ?? null;
      if (scope) {
        saveUserScopeToStorage(scope, userId);
        setUserScope(scope);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error loading user scope:", error);
      }
    }
  }, [getAccessTokenSilently, userId, setUserScope]);

  const loadProfile = useCallback(async () => {
    try {
      const response = await tripService.getMyProfile();
      const profile = response.data?.data ?? null;
      if (profile) {
        setDriverProfile(profile);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error loading driver profile:", error);
      }
    }
  }, [setDriverProfile]);

  useEffect(() => {
    if (!isAuthenticated || !isApiClientReady || !userId || !conRol) return;
    const run = async () => {
      if (hasLoadedScopeRef.current) return;
      hasLoadedScopeRef.current = true;
      await Promise.all([loadScope(), loadProfile()]);
    };
    run();
  }, [isAuthenticated, isApiClientReady, userId, conRol, loadScope, loadProfile]);

  useEffect(() => {
    const handler = async () => {
      if (!conRol) return;
      hasLoadedScopeRef.current = false;
      clearUserScopeFromStorage(userId);
      await loadScope();
    };
    window.addEventListener(SCOPE_INVALIDATED_EVENT, handler);
    return () => window.removeEventListener(SCOPE_INVALIDATED_EVENT, handler);
  }, [userId, conRol, loadScope]);

  return null;
};
